import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Divider
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Google as GoogleIcon
} from '@mui/icons-material';
import { apiService } from '../services/apiService';
import { authService } from '../services/authService';
import BrandLogo from '../components/Brand/BrandLogo';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [userInfo, setUserInfo] = useState(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid invitation link');
      return;
    }

    // Validate token and get user info
    const validateToken = async () => {
      try {
        const response = await apiService.get(`/auth/validate-invitation/${token}`);
        setUserInfo(response.data.user);
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired invitation link');
      }
    };

    validateToken();
  }, [token]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      await apiService.post('/auth/setup-password', {
        token,
        password: formData.password
      });
      
      setSuccess('Password set successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to set password');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    try {
      setLoading(true);
      const result = await authService.activateInvitation(token);
      setSuccess(`Invitation accepted. Sign in with the Google account for ${result.email}…`);
      await authService.loginWithGoogle();
      navigate('/dashboard');
    } catch (err) {
      setSuccess('');
      setError(err.message || 'Google sign-in failed');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        sx={{ backgroundColor: 'background.default', p: 3 }}
      >
        <Alert severity="error">
          Invalid invitation link. Please contact your administrator.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ backgroundColor: 'background.default', p: 3 }}
    >
      <Box sx={{ width: '100%', maxWidth: 500 }}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          sx={{
            mb: 3,
          }}
        >
          <BrandLogo height={64} />
        </Box>

        <Card>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom align="center">
              Set Up Your Password
            </Typography>
            
            {userInfo && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Welcome, {userInfo.firstName} {userInfo.lastName}!
              </Typography>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Password must be at least 8 characters long"
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                margin="normal"
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Set Password'}
              </Button>
            </Box>

            <Divider sx={{ my: 2 }}>or</Divider>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<GoogleIcon />}
              onClick={handleGoogle}
              disabled={loading}
            >
              Continue with Google instead
            </Button>
            <Typography variant="caption" color="text.secondary" display="block" align="center" sx={{ mt: 1 }}>
              Use the Google account that matches your invited email address.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SetupPassword;
